import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextarea } from 'primeng/inputtextarea';
import { StaffDisputeDetail } from '../../services/staff-dispute.service';

export type ReviewStatusOption = 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';

@Component({
    selector: 'app-dispute-review-drawer',
    standalone: true,
    imports: [CommonModule, FormsModule, DialogModule, ButtonModule, SelectModule, InputTextarea],
    template: `
        <p-dialog
            header="Update Dispute Status"
            [(visible)]="visible"
            (visibleChange)="onVisibleChange($event)"
            position="right"
            [modal]="true"
            [draggable]="false"
            [resizable]="false"
            [closable]="true"
            [dismissableMask]="true"
            [blockScroll]="true"
            styleClass="dispute-review-drawer-dialog"
            maskStyleClass="dispute-review-drawer-mask"
            [style]="dialogStyle"
            [contentStyle]="{ overflow: 'auto', padding: '0 1.25rem 1.25rem' }"
        >
            <div class="drawer-body" *ngIf="detail">
                <section class="drawer-section">
                    <label>Customer Message</label>
                    <p class="readonly">{{ detail.customerComment || '—' }}</p>
                </section>

                <section class="drawer-section" *ngIf="detail.attachments?.length">
                    <label>Attachments</label>
                    <div class="drawer-thumb" *ngFor="let a of detail.attachments">
                        <img
                            *ngIf="isImage(a) && previewUrls.get(a.id) as url"
                            [src]="url"
                            [alt]="a.originalFileName"
                        />
                        <span>{{ a.originalFileName }}</span>
                    </div>
                </section>

                <section class="drawer-section">
                    <label for="nextStatus">Update Status</label>
                    <p-select
                        inputId="nextStatus"
                        [options]="statusOptions"
                        [(ngModel)]="selectedStatus"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select status"
                        appendTo="body"
                    ></p-select>
                </section>

                <section class="drawer-section">
                    <label for="internalComment">Internal Notes</label>
                    <textarea id="internalComment" pInputTextarea [(ngModel)]="internalComment" [rows]="3"></textarea>
                </section>

                <section class="drawer-section">
                    <label for="customerMessage">Message To Customer</label>
                    <p class="field-hint" *ngIf="selectedStatus === 'UNDER_REVIEW'">
                        Optional when marking under review. Required for approve/reject.
                    </p>
                    <p class="field-hint required" *ngIf="selectedStatus === 'APPROVED' || selectedStatus === 'REJECTED'">
                        Required — shown to the customer in the mobile app (min 10 characters).
                    </p>
                    <textarea
                        id="customerMessage"
                        pInputTextarea
                        [(ngModel)]="customerMessage"
                        [rows]="4"
                        placeholder="Shown in customer app"
                    ></textarea>
                </section>

                <div class="drawer-footer">
                    <button pButton type="button" label="Cancel" severity="secondary" (click)="close()"></button>
                    <button
                        pButton
                        type="button"
                        label="Save Update"
                        class="btn-primary-dispute"
                        (click)="save.emit({ status: selectedStatus, customerMessage, internalComment })"
                    ></button>
                </div>
            </div>
        </p-dialog>
    `,
    styles: [
        `
            .drawer-body {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }
            .drawer-section {
                display: flex;
                flex-direction: column;
                gap: 0.35rem;
            }
            .drawer-section label {
                font-size: 0.8rem;
                font-weight: 600;
                color: var(--p-text-muted-color, var(--text-color-secondary));
                text-transform: uppercase;
            }
            .field-hint {
                margin: 0;
                font-size: 0.78rem;
                color: var(--dispute-muted, var(--text-color-secondary));
            }
            .field-hint.required {
                color: var(--p-orange-600);
            }
            .readonly {
                margin: 0;
                padding: 0.65rem 0.75rem;
                background: var(--dispute-nested-bg, var(--surface-secondary));
                border-radius: 8px;
                border: 1px solid var(--dispute-border, var(--surface-border));
            }
            .drawer-thumb {
                display: flex;
                flex-direction: column;
                gap: 0.35rem;
            }
            .drawer-thumb img {
                max-height: 120px;
                object-fit: contain;
                border-radius: 6px;
                border: 1px solid var(--dispute-border);
            }
            .drawer-footer {
                display: flex;
                gap: 0.75rem;
                justify-content: flex-end;
                margin-top: 0.5rem;
            }
        `
    ]
})
export class DisputeReviewDrawerComponent {
    @Input({ required: true }) detail: StaffDisputeDetail | null = null;
    @Input() previewUrls: Map<number, string> = new Map();
    @Input() statusOptions: { label: string; value: ReviewStatusOption }[] = [];

    readonly dialogStyle: Record<string, string> = {
        width: 'min(420px, 100vw)',
        height: '100vh',
        maxHeight: '100vh',
        margin: '0',
        borderRadius: '0'
    };

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() closed = new EventEmitter<void>();
    @Output() save = new EventEmitter<{
        status: ReviewStatusOption | '';
        customerMessage: string;
        internalComment: string;
    }>();

    selectedStatus: ReviewStatusOption | '' = '';
    customerMessage = '';
    internalComment = '';
    visible = false;

    @Input()
    set drawerOpen(value: boolean) {
        this.visible = value;
        if (value) {
            const preferred =
                this.statusOptions.find((o) => o.value === 'APPROVED') || this.statusOptions[0];
            this.selectedStatus = preferred?.value || '';
            this.customerMessage = '';
            this.internalComment = '';
        }
    }

    isImage(a: { contentType?: string }): boolean {
        return (a.contentType || '').toLowerCase().startsWith('image/');
    }

    close(): void {
        this.onVisibleChange(false);
    }

    onVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
        if (!value) {
            this.closed.emit();
        }
    }
}
